import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { Party } from "@/lib/types";
import { useSession } from "@/lib/auth";
import { OrdermailForm } from "@/features/orders/OrdermailForm";
import { listOrderListTemplates, orderListTemplateKeys, type OrderListTemplate } from "@/features/orders/orderListTemplates.api";

// The tab Ordermail on a relation card (docs/invoerbak.md §3b): how this customer mails its orders, beside the tab
// Portaal that says how it enters them online. Per ordermail its senders, whether its mail goes into the invoerbak by
// itself, and whether WLX reads it as a list or as a plain mail, with its columns. Made and changed here only, and by an
// admin only (Camiel, 25-10-2026: "Ik wil niet dat iemand anders behalve een beheerder dit kan doen, en dit moet vanuit
// de relatie"); whoever works the bak sees them.

export function PartyOrdermails({ party }: { party: Party }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const templates = useQuery({ queryKey: orderListTemplateKeys.party(party.id), queryFn: () => listOrderListTemplates(party.id) });
  const [editing, setEditing] = useState<OrderListTemplate | "new" | null>(null);
  const session = useSession();
  const admin = Boolean(session.data?.roles.includes("admin"));
  const saved = () => {
    setEditing(null);
    void queryClient.invalidateQueries({ queryKey: orderListTemplateKeys.all });
  };
  return (
    <div className="wl-vstack" style={{ gap: "var(--space-3)", alignItems: "stretch", maxWidth: editing ? "1560px" : "980px" }}>
      <div className="wl-hstack" style={{ gap: "8px", alignItems: "center" }}>
        <span className="t-caption t-muted">{t("intake.partyTabHelp")}</span>
        <span className="wl-spacer" />
        {admin ? (
          <button type="button" className="wl-btn wl-btn--accent wl-btn--sm" disabled={editing !== null} onClick={() => setEditing("new")}>
            {t("orders.mailChannelNew")}
          </button>
        ) : (
          <span className="t-caption t-muted">{t("intake.partyTabAdminOnly")}</span>
        )}
      </div>
      {editing && admin && <OrdermailForm current={editing === "new" ? undefined : editing} party={party} onClose={() => setEditing(null)} onSaved={saved} onDeleted={saved} />}
      {templates.isLoading && <span className="t-caption t-muted">{t("common.loading")}</span>}
      {templates.data && templates.data.length === 0 && !editing && <span className="t-body-sm t-muted">{t("intake.partyTabEmpty")}</span>}
      {templates.data && templates.data.length > 0 && (
        <div className="wl-table-wrap" style={{ maxHeight: "none" }}>
          <table className="wl-table wl-table--compact">
            <thead>
              <tr>
                <th>{t("orders.listTemplateName")}</th>
                <th>{t("orders.mailChannelSenders")}</th>
                <th>{t("orders.mailChannelReadAs")}</th>
                <th>{t("orders.mailChannelAutoIntake")}</th>
                <th>{t("orders.ordermailRoute")}</th>
                <th aria-label={t("orders.mailChannelEdit")} />
              </tr>
            </thead>
            <tbody>
              {templates.data.map((template) => (
                <tr key={template.id}>
                  <td>{template.name}</td>
                  <td className="t-caption">
                    {template.senders.join(", ") || "—"}
                    {template.subjectFilter ? ` · ${t("intake.subjectHas", { text: template.subjectFilter })}` : ""}
                  </td>
                  <td>{template.readAs === "mail" ? t("orders.mailChannelReadAsMail") : t("orders.mailChannelReadAsList")}</td>
                  <td>{template.autoIntake ? t("common.yes") : t("common.no")}</td>
                  <td className="t-caption">{`${template.load ? template.load.name : t("orders.ordermailPerRow")} → ${template.unload ? template.unload.name : t("orders.ordermailPerRow")}`}</td>
                  <td style={{ textAlign: "right" }}>
                    {admin && (
                      <button type="button" className="wl-btn wl-btn--ghost wl-btn--sm" disabled={editing !== null} onClick={() => setEditing(template)}>
                        {t("orders.mailChannelEdit")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
